import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bot,
  ExternalLink,
  Eye,
  Inbox as InboxIcon,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import ThreadPreview from "@/components/inbox/ThreadPreview";
import { cn } from "@/lib/utils";
import { CATEGORY_STYLES, inr, initials, relTime, shortDate } from "@/lib/format";
import { errMessage, extractThread, gmailStatus, gmailSync, listInbox } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { useIsDesktop } from "@/hooks/use-media-query";

const TABS = ["All", "Advance Payment", "Needs Attention", "Approval Replies", "Other"];

// Thread-level state of the background approval worker.
const AUTO_BADGES = {
  queued: {
    label: "Queued for extraction",
    cls: "border-info-border bg-info-surface text-info-text",
    spin: true,
  },
  running: {
    label: "Extracting…",
    cls: "border-success-border bg-success-surface text-success-text",
    spin: true,
  },
  failed: {
    label: "Extraction failed",
    cls: "border-danger-border bg-danger-surface text-danger-text",
    spin: false,
  },
  interrupted: {
    label: "Extraction interrupted",
    cls: "border-warning-border bg-warning-surface text-warning-text",
    spin: false,
  },
};

export default function InboxPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // The two-pane layout takes over at xl; below that the preview opens in a drawer.
  const isDesktop = useIsDesktop();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [extractingId, setExtractingId] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(false);

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const [st, data] = await Promise.all([
          gmailStatus().catch(() => null),
          listInbox({ category: opts.tab === "All" ? "" : (opts.tab ?? tab), q: opts.q ?? q }),
        ]);
        if (st) setStatus(st);
        setItems(data.items || []);
        setCounts(data.counts || {});
      } catch (e) {
        toast.error("Could not load the inbox", { description: errMessage(e) });
      } finally {
        setLoading(false);
      }
    },
    [tab, q]
  );

  useEffect(() => {
    load({ tab, q: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    const t = setTimeout(() => load({ q }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    const onSynced = () => load();
    window.addEventListener("gmail-synced", onSynced);
    return () => window.removeEventListener("gmail-synced", onSynced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q]);

  // Growing past xl reveals the sticky pane, so the drawer (and its backdrop) must go.
  useEffect(() => {
    if (isDesktop && mobilePreview) setMobilePreview(false);
  }, [isDesktop, mobilePreview]);

  // While the approval worker is busy on any visible thread, keep the list fresh so the
  // "Extracting…" badge turns into "N in queue" on its own.
  const autoBusy = useMemo(
    () => items.some((i) => ["queued", "running"].includes(i.auto_extract_status || "")),
    [items]
  );

  useEffect(() => {
    if (!autoBusy) return undefined;
    const id = setInterval(() => {
      listInbox({ category: tab === "All" ? "" : tab, q })
        .then((data) => {
          setItems(data.items || []);
          setCounts(data.counts || {});
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [autoBusy, tab, q]);

  const openThread = (threadId) => {
    setSelected(threadId);
    // Only below xl: on desktop the preview is already visible in the right pane and
    // opening the drawer would just dim the whole page with its backdrop.
    setMobilePreview(!isDesktop);
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await gmailSync(false);
      if (res?.skipped) toast("A sync is already running");
      else toast.success("Gmail sync complete", { description: res?.summary });
      await load();
    } catch (e) {
      toast.error("Sync failed", { description: errMessage(e) });
    } finally {
      setSyncing(false);
    }
  };

  const runExtract = async (threadId) => {
    setExtractingId(threadId);
    try {
      const res = await extractThread(threadId);
      setItems((prev) =>
        prev.map((it) =>
          it.thread_id === threadId ? { ...it, extracted_row_count: res.row_count } : it
        )
      );
      if (res.row_count === 0) {
        toast.warning("No vendor details found in this mail", {
          description: "Open the thread to check the body and attachments.",
        });
      } else {
        toast.success(`${res.row_count} vendor row${res.row_count === 1 ? "" : "s"} added to the queue`, {
          description: res.files_used?.length
            ? `Read ${res.files_used.length} attachment(s): ${res.files_used.join(", ")}`
            : "Extracted from the mail body.",
          action: { label: "Open Queue", onClick: () => navigate("/queue") },
        });
      }
      if (res.attachment_errors?.length) {
        toast.error("Some attachments could not be read", {
          description: res.attachment_errors.join(" · "),
        });
      }
    } catch (e) {
      toast.error("Extraction failed", { description: errMessage(e) });
    } finally {
      setExtractingId(null);
    }
  };

  const grouped = useMemo(() => {
    if (tab !== "All") return [{ category: tab, rows: items }];
    const order = ["Advance Payment", "Needs Attention", "Approval Replies", "Other"];
    return order
      .map((c) => ({ category: c, rows: items.filter((i) => (i.category || "Other") === c) }))
      .filter((g) => g.rows.length);
  }, [items, tab]);

  const notConnected = status && !status.connected;

  return (
    /* At xl the AppShell main is locked to the viewport, so this column just fills
       it and the two panes own their scrolling. Below xl it stacks and the page
       scrolls normally. */
    <div
      className="flex flex-col gap-4 xl:min-h-0 xl:flex-1 xl:overflow-hidden"
      data-testid="inbox-page"
    >
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold tracking-tight sm:text-xl">Payments inbox</h1>
          <p className="max-w-[44rem] truncate text-xs text-muted-foreground sm:text-sm xl:max-w-[60rem]">
            Synced mails triaged by AI into four buckets.{" "}
            {status?.effective_query ? (
              <span
                className="vde-code hidden text-[11px] sm:inline"
                title={status.effective_query}
              >
                {status.effective_query}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status?.connected && isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 bg-card"
              onClick={syncNow}
              disabled={syncing}
              data-testid="inbox-sync-button"
            >
              {syncing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Sync now
            </Button>
          ) : null}
          {isAdmin ? (
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-card">
              <Link to="/settings" data-testid="inbox-settings-link">
                <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" /> Settings
              </Link>
            </Button>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
              data-testid="inbox-readonly-chip"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Read-only access
            </span>
          )}
        </div>
      </div>

      {notConnected ? (
        <section className="vde-card shrink-0 p-5" data-testid="gmail-not-connected-card">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-warning-border bg-warning-surface">
              <Mail className="h-5 w-5 text-warning-text" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold">
                {status?.configured ? "Gmail is not connected yet" : "Gmail is not configured yet"}
              </h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
                {status?.configured
                  ? "Connect the payments mailbox to start syncing every 5 minutes - both group mail and anything sent straight to it. Threads are classified automatically as they arrive."
                  : "Add your Google OAuth client ID and secret to the server, then connect the payments mailbox. Until then you can still use Manual Extract, the Queue and Export."}
              </p>
              {isAdmin ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm" className="gap-1.5">
                    <Link to="/settings" data-testid="inbox-connect-cta">
                      <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" /> Open Gmail settings
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="gap-1.5 bg-card">
                    <Link to="/extract">Use manual extract instead</Link>
                  </Button>
                </div>
              ) : (
                <p className="mt-3 text-xs font-medium text-warning-text">
                  Ask an admin to connect the payments mailbox from Settings.
                </p>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* category tabs — pinned above both scroll regions */}
      <div className="vde-card shrink-0 p-2.5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Mail categories">
            {TABS.map((t) => {
              const c = t === "All" ? counts.All : counts[t];
              const active = tab === t;
              const style = CATEGORY_STYLES[t];
              return (
                <button
                  key={t}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t)}
                  data-testid={`inbox-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-foreground hover:bg-secondary"
                  )}
                >
                  {style && !active ? (
                    <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} aria-hidden="true" />
                  ) : null}
                  {t}
                  <span
                    className={cn(
                      "vde-code rounded-full px-1.5 text-[10px]",
                      active ? "bg-primary-foreground/20" : "bg-secondary"
                    )}
                  >
                    {c ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-80">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sender, subject, vendor…"
              className="h-9 bg-card pl-8 text-xs"
              data-testid="inbox-search-input"
            />
          </div>
        </div>
      </div>

      {/* two-pane region: 56% list / 44% preview at xl, each scrolling on its own */}
      <div className="grid min-w-0 grid-cols-1 items-start gap-4 xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,1fr)_minmax(420px,44%)] xl:items-stretch">
        {/* LEFT PANE — thread list */}
        <section
          className="min-w-0 xl:flex xl:min-h-0 xl:flex-col xl:overflow-hidden"
          aria-label="Payments inbox list"
        >
          <div
            className="vde-scrollbar-brand min-w-0 space-y-3 scroll-pt-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-contain xl:pr-2"
            data-testid="inbox-thread-list-scroll"
          >
            {loading ? (
              <div className="vde-card flex items-center justify-center gap-2 p-10 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading mails…
              </div>
            ) : null}

            {!loading && !items.length ? (
              <div className="vde-card p-8 text-center" data-testid="inbox-empty">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
                  <InboxIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold">
                  {status?.connected ? "No mails in this bucket yet" : "Nothing synced yet"}
                </p>
                <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
                  {status?.connected
                    ? "Try another category, clear the search, or run a sync to pull the latest payment mail."
                    : "Once the payments mailbox is connected, matching payment mails from the last 30 days appear here automatically."}
                </p>
                <div className="vde-brand-stripe mx-auto mt-4 w-32 rounded-full" aria-hidden="true" />
              </div>
            ) : null}

            {grouped.map((group) => (
              <section key={group.category} className="space-y-2">
                {tab === "All" ? (
                  <h2 className="flex items-center gap-2 bg-background/90 px-1 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground backdrop-blur-sm xl:sticky xl:top-0 xl:z-10">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        (CATEGORY_STYLES[group.category] || CATEGORY_STYLES.Other).dot
                      )}
                      aria-hidden="true"
                    />
                    {group.category}
                    <span className="vde-code">({group.rows.length})</span>
                  </h2>
                ) : null}

                <ul
                  className="space-y-2"
                  data-testid={`inbox-group-${group.category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {group.rows.map((it) => {
                    const cat = CATEGORY_STYLES[it.category] || CATEGORY_STYLES.Other;
                    const active = selected === it.thread_id;
                    return (
                      <li
                        key={it.thread_id}
                        className={cn(
                          "vde-card p-3 transition-colors",
                          active
                            ? "border-primary bg-primary/[0.06]"
                            : "hover:border-primary/30 hover:bg-secondary/60"
                        )}
                        data-testid="inbox-row"
                        data-selected={active ? "true" : "false"}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                            onClick={() => openThread(it.thread_id)}
                            data-testid={`inbox-open-${it.thread_id}`}
                          >
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground">
                              {initials(it.from_name || it.from_email)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-baseline gap-2">
                                <span className="truncate text-[12.5px] font-semibold" data-testid="inbox-sender">
                                  {it.from_name || it.from_email}
                                </span>
                                <span
                                  className="vde-code ml-auto shrink-0 text-[10px] text-muted-foreground"
                                  title={it.last_message_at || ""}
                                  data-testid="inbox-date"
                                >
                                  {shortDate(it.last_message_at)} · {relTime(it.last_message_at)}
                                </span>
                              </span>
                              <span className="mt-0.5 block truncate text-[13px] font-medium" data-testid="inbox-subject">
                                {it.subject}
                              </span>
                              <span className="mt-0.5 line-clamp-1 block text-[11px] text-muted-foreground">
                                {it.ai_summary || it.snippet}
                              </span>

                              <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                <span
                                  className={cn(
                                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                                    cat.badge
                                  )}
                                  data-testid="inbox-category-badge"
                                >
                                  <Bot className="h-2.5 w-2.5" aria-hidden="true" /> {it.category}
                                </span>
                                {it.approved_amount ? (
                                  <span className="vde-code inline-flex items-center rounded-full border border-success-border bg-success-surface px-2 py-0.5 text-[10px] font-semibold text-success-text">
                                    ₹{inr(it.approved_amount)} approved
                                  </span>
                                ) : it.requested_amount ? (
                                  <span className="vde-code inline-flex items-center rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px]">
                                    ₹{inr(it.requested_amount)} requested
                                  </span>
                                ) : null}
                                {it.approver_email ? (
                                  <span className="vde-code max-w-[170px] truncate rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground">
                                    {it.approver_email}
                                  </span>
                                ) : null}
                                {it.attachment_count ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                    <Paperclip className="h-2.5 w-2.5" aria-hidden="true" />
                                    {it.attachment_count}
                                  </span>
                                ) : null}
                                {it.message_count > 1 ? (
                                  <span className="text-[10px] text-muted-foreground">
                                    {it.message_count} msgs
                                  </span>
                                ) : null}
                                {it.extracted_row_count ? (
                                  <span className="inline-flex items-center rounded-full border border-info-border bg-info-surface px-2 py-0.5 text-[10px] font-semibold text-info-text">
                                    {it.extracted_row_count} in queue
                                  </span>
                                ) : null}
                                {!it.extracted_row_count && AUTO_BADGES[it.auto_extract_status] ? (
                                  <span
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                                      AUTO_BADGES[it.auto_extract_status].cls
                                    )}
                                    title={it.auto_extract_error || AUTO_BADGES[it.auto_extract_status].label}
                                    data-testid={`inbox-auto-status-${it.thread_id}`}
                                  >
                                    {AUTO_BADGES[it.auto_extract_status].spin ? (
                                      <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
                                    ) : (
                                      <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
                                    )}
                                    {AUTO_BADGES[it.auto_extract_status].label}
                                  </span>
                                ) : null}
                                {it.classification_error ? (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-danger-border bg-danger-surface px-2 py-0.5 text-[10px] font-semibold text-danger-text">
                                    <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" /> AI error
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          </button>

                          <div className="flex shrink-0 flex-col items-end gap-1.5">
                            {isAdmin ? (
                              <Button
                                size="sm"
                                className="h-8 gap-1.5 px-2.5 text-[11px] active:scale-[0.98]"
                                onClick={() => runExtract(it.thread_id)}
                                disabled={
                                  extractingId === it.thread_id ||
                                  ["queued", "running"].includes(it.auto_extract_status || "")
                                }
                                data-testid={`inbox-extract-${it.thread_id}`}
                              >
                                {extractingId === it.thread_id ||
                                ["queued", "running"].includes(it.auto_extract_status || "") ? (
                                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                                ) : (
                                  <Wand2 className="h-3 w-3" aria-hidden="true" />
                                )}
                                Extract
                              </Button>
                            ) : null}
                            {it.gmail_link ? (
                              <a
                                href={it.gmail_link}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 rounded text-[10px] text-muted-foreground transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                data-testid="inbox-gmail-link"
                              >
                                <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" /> Gmail
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </section>

        {/* RIGHT PANE — opened mail, scrolls independently. Mounted only at xl so the
            thread is not fetched twice and the mobile drawer stays the single source. */}
        {isDesktop ? (
          <aside
            className="hidden min-w-0 xl:flex xl:min-h-0 xl:flex-col xl:overflow-hidden"
            aria-label="Opened thread preview"
          >
            <ThreadPreview
              threadId={selected}
              canEdit={isAdmin}
              onExtract={runExtract}
              extracting={extractingId === selected}
              onThreadUpdated={(updated) =>
                setItems((prev) =>
                  prev.map((it) => (it.thread_id === updated.thread_id ? { ...it, ...updated } : it))
                )
              }
            />
          </aside>
        ) : null}
      </div>

      {/* preview (mobile / tablet) - never mounted on desktop: the Radix overlay is
          full-screen and would dim the inbox even with the panel hidden. */}
      {!isDesktop ? (
        <Sheet open={mobilePreview && !!selected} onOpenChange={setMobilePreview}>
          <SheetContent
            side="right"
            className="w-full max-w-[560px] bg-background p-0 sm:max-w-[560px] [&>button:first-child]:hidden"
            data-testid="thread-preview-drawer"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Mail preview</SheetTitle>
            </SheetHeader>
            <div className="h-full p-3">
              <ThreadPreview
                threadId={selected}
                canEdit={isAdmin}
                onClose={() => setMobilePreview(false)}
                onExtract={runExtract}
                extracting={extractingId === selected}
                onThreadUpdated={(updated) =>
                  setItems((prev) =>
                    prev.map((it) => (it.thread_id === updated.thread_id ? { ...it, ...updated } : it))
                  )
                }
              />
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
